import React from 'react';
import { useNavigate } from 'react-router-dom';

const ErrorPage = ({ onGoHome, onGoLanding, errorCode = 404 }) => {
    const navigate = useNavigate();

    const getErrorDetails = () => {
        switch (errorCode) {
            case 401:
                return {
                    title: "Unauthorized Access",
                    message: "Please log in to access this page.",
                    icon: "ri-shield-keyhole-line",
                    color: "text-orange-400"
                };
            case 404:
                return {
                    title: "Page Not Found",
                    message: "Oops! The page you're looking for doesn't exist.",
                    icon: "ri-map-pin-line",
                    color: "text-yellow-400"
                };
            case 403:
                return {
                    title: "Access Denied",
                    message: "You don't have permission to access this page.",
                    icon: "ri-shield-keyhole-line",
                    color: "text-red-400"
                };
            case 500:
                return {
                    title: "Server Error",
                    message: "Something went wrong on our server. Please try again later.",
                    icon: "ri-server-line",
                    color: "text-purple-400"
                };
            default:
                return {
                    title: "Something Went Wrong",
                    message: "An unexpected error occurred. Please try again.",
                    icon: "ri-error-warning-line",
                    color: "text-blue-400"
                };
        }
    };

    const errorDetails = getErrorDetails();

    const handleGoHome = () => {
        if (onGoHome) {
            onGoHome();
        } else {
            navigate('/home');
        }
    };

    const handleGoLanding = () => {
        if (onGoLanding) {
            onGoLanding();
        } else {
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
            <div className="text-center max-w-md">
                {/* Error Icon */}
                <div className="mb-8">
                    <div className="w-32 h-32 bg-gray-800 rounded-full flex items-center justify-center mx-auto border-4 border-gray-700 relative">
                        <i className={`${errorDetails.icon} ${errorDetails.color} text-4xl`}></i>

                        {/* Animated Rings */}
                        <div className="absolute inset-0 border-4 border-yellow-500/30 rounded-full animate-ping"></div>
                        <div className="absolute inset-2 border-4 border-red-500/20 rounded-full animate-pulse"></div>
                    </div>
                </div>

                {/* Error Code */}
                <div className="mb-4">
                    <span className="text-6xl font-bold bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent">
                        {errorCode}
                    </span>
                </div>

                {/* Error Title */}
                <h1 className="text-2xl font-bold text-white mb-4">
                    {errorDetails.title}
                </h1>

                {/* Error Message */}
                <p className="text-gray-400 mb-8 leading-relaxed">
                    {errorDetails.message}
                </p>



                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 border border-gray-700 hover:border-gray-600 flex items-center justify-center space-x-2"
                    >
                        <i className="ri-arrow-left-line"></i>
                        <span>Go Back</span>
                    </button>

                    <button
                        onClick={handleGoHome}
                        className="bg-gradient-to-r from-red-500 to-yellow-500 hover:from-red-600 hover:to-yellow-600 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                    >
                        <i className="ri-home-line"></i>
                        <span>Go Home</span>
                    </button>

                    <button
                        onClick={handleGoLanding}
                        className="bg-gray-700 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 border border-gray-600 flex items-center justify-center space-x-2"
                    >
                        <i className="ri-login-box-line"></i>
                        <span>Landing Page</span>
                    </button>
                </div>

                {/* Additional Help */}
                <div className="mt-8 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                    <h3 className="text-white font-semibold mb-2 flex items-center justify-center space-x-2">
                        <i className="ri-customer-service-2-line text-blue-400"></i>
                        <span>Need Help?</span>
                    </h3>
                    <p className="text-gray-400 text-sm mb-3">
                        Contact our support team if the problem persists.
                    </p>
                    <button
                        onClick={() => alert('Support: support@ffarena.com')}
                        className="text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors"
                    >
                        Contact Support <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                </div>
            </div>

            {/* Floating Elements */}
            <div className="fixed top-10 left-10 w-3 h-3 bg-yellow-400 rounded-full opacity-60 animate-bounce"></div>
            <div className="fixed top-20 right-20 w-2 h-2 bg-red-400 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="fixed bottom-20 left-20 w-3 h-3 bg-blue-400 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            <div className="fixed bottom-10 right-10 w-2 h-2 bg-green-400 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.6s' }}></div>
        </div>
    );
};

export default ErrorPage;